import { LiveAnnouncer } from '@angular/cdk/a11y';
import { InjectionToken, OnDestroy, } from '@angular/core';
import { AppStateFacade } from '@app-state';
import { buildParentMap, emptyFolderContainers, getParentIds, patchContainers, trimExpandedFolders, } from '@components/ui-folder-tree/helpers';
import { TranslateService } from '@ngx-translate/core';
import { FoldersService } from '@services';
import { VirtualScrollItemStatus } from '@uipath/angular/directives/ui-virtual-scroll-range-loader';
import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, combineLatest, of, Subject, } from 'rxjs';
import { finalize, map, retry, skip, takeUntil, tap, } from 'rxjs/operators';
export var FOLDER_TREE_SOURCE = new InjectionToken('Folder-Tree-Source');
export var FOLDER_FETCH_SIZE = 50;
export var INITIAL_SKIP_OFFSET = -FOLDER_FETCH_SIZE / 2;
var FolderTreeStateService = /** @class */ (function () {
    function FolderTreeStateService(_folderSource, _appState, _translateService, _liveAnnouncer, folderService) {
        var _this = this;
        this._folderSource = _folderSource;
        this._appState = _appState;
        this._translateService = _translateService;
        this._liveAnnouncer = _liveAnnouncer;
        this.parentIdOfExpandedId = new Map();
        this.highlighted$ = new BehaviorSubject(null);
        this.activeIndex = 0;
        this.initialLoading$ = new BehaviorSubject(true);
        this.loadingFolder$ = new BehaviorSubject({ value: false });
        this.searching$ = new BehaviorSubject(false);
        this._searchValue$ = new BehaviorSubject('');
        this._destroyed$ = new Subject();
        this._patchStarted$ = new Subject();
        this._treeContainers$ = new BehaviorSubject([]);
        this._searchContainers$ = new BehaviorSubject([]);
        this._containers$ = new BehaviorSubject([]);
        this.switchToHighlighted = function () {
            var highlightedFolder = _this.highlighted$.value;
            if (!_this._folderSource.hasSwitchCapability ||
                !highlightedFolder.isSelectable ||
                highlightedFolder.id === _this.selectedFolderId) {
                return of(false);
            }
            return _this._appState.user.folder
                .set(highlightedFolder.id).pipe(tap(function () {
                var folder = _this._appState.user.folder.value;
                if (_this.isInSearchMode &&
                    folder.folderContext != null) {
                    _this._initialContextSetup(folder.folderContext, 0);
                }
            }));
        };
        this.isExpanded = function (nodeId) {
            return _this.parentIdOfExpandedId.has(nodeId);
        };
        this._patch = function (skipItems, take, invalidate) {
            if (invalidate === void 0) { invalidate = function (_) { return false; }; }
            _this._patchStarted$.next();
            var fetch$ = _this._folderSource.getFolders(trimExpandedFolders(_this.parentIdOfExpandedId), take, skipItems);
            return fetch$.pipe(retry(1), map(function (_a) {
                var folders = _a.data, count = _a.total;
                var shouldInvalidate = invalidate(count);
                var newContainers = shouldInvalidate ? emptyFolderContainers(count) : _this.treeContainers;
                patchContainers(folders, skipItems, newContainers);
                _this.treeContainers = newContainers;
                _this._checkHighlightValid();
                return shouldInvalidate;
            }), takeUntil(_this._destroyed$));
        };
        this._initialContextSetup = function (_a, offset) {
            var ancestors = _a.ancestors, highlightIndex = _a.index, folders = _a.folders, count = _a.count;
            if (offset === void 0) { offset = INITIAL_SKIP_OFFSET; }
            var containers = emptyFolderContainers(count);
            patchContainers(folders, Math.max(0, highlightIndex + offset), containers);
            _this.treeContainers = containers;
            _this.parentIdOfExpandedId = buildParentMap(ancestors);
            _this.activeIndex = Math.max(0, highlightIndex - 5);
            var folderData = _this.treeContainers[highlightIndex]
                ? _this.treeContainers[highlightIndex].folderData
                : null;
            if (folderData) {
                _this.highlighted$.next(folderData);
            }
            _this.initialLoading$.next(false);
        };
        this._searchFolders = function (searchTerm, fetchSize, skipItems) {
            if (skipItems === void 0) { skipItems = 0; }
            _this.searchContainers
                .slice(skipItems, skipItems + fetchSize)
                .forEach(function (f) { return f.loading = VirtualScrollItemStatus.pending; });
            _this._folderSource.searchFolders(skipItems, fetchSize, searchTerm)
                .pipe(takeUntil(_this._searchValue$.pipe(skip(1))), finalize(function () { return _this.searching$.next(false); }))
                .subscribe(function (_a) {
                var total = _a.total, data = _a.data;
                data.forEach(function (f) {
                    f.level = 1;
                    f.isSelectable = true;
                });
                var flushContainers = skipItems === 0 || total !== _this.searchContainers.length;
                var containers = flushContainers ? emptyFolderContainers(total) : _this.searchContainers;
                patchContainers(data, skipItems, containers);
                _this.searchContainers = containers;
            });
        };
        if (!this._folderSource) {
            this._folderSource = folderService;
        }
        this.getInitialContext()
            .pipe(takeUntil(this._destroyed$)).subscribe(this._initialContextSetup);
        combineLatest([
            this._treeContainers$,
            this._searchContainers$,
            this._searchValue$,
        ]).pipe(takeUntil(this._destroyed$)).subscribe(function (_a) {
            var treeContainers = _a[0], searchContainers = _a[1], searchValue = _a[2];
            return _this._containers$.next(!!searchValue ? searchContainers : treeContainers);
        });
    }
    Object.defineProperty(FolderTreeStateService.prototype, "isInSearchMode", {
        get: function () {
            return !!this.searchValue;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "searchValue", {
        get: function () {
            return this._searchValue$.value;
        },
        set: function (value) {
            this._searchValue$.next(value);
            if (!value) {
                this.refresh();
            }
            else {
                this.searching$.next(true);
                this._searchFolders(value, FOLDER_FETCH_SIZE, 0);
            }
            this.highlighted$.next(null);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "selectedFolderId", {
        get: function () {
            if (!this._folderSource.hasSwitchCapability) {
                return null;
            }
            var folderManager = this._appState.user.folder;
            return folderManager.hasAny ? folderManager.value.id : null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "selectedFolder", {
        get: function () {
            if (this.selectedFolderId === null) {
                return null;
            }
            return this._appState.user.folder.value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "treeContainers", {
        get: function () {
            return this._treeContainers$.value;
        },
        set: function (containers) {
            this._treeContainers$.next(containers);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "searchContainers", {
        get: function () {
            return this._searchContainers$.value;
        },
        set: function (containers) {
            this._searchContainers$.next(containers);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "containers$", {
        get: function () {
            return this._containers$;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FolderTreeStateService.prototype, "containers", {
        get: function () {
            return this._containers$.value;
        },
        enumerable: true,
        configurable: true
    });
    FolderTreeStateService.prototype.getInitialContext = function () {
        if (!this._folderSource.hasSwitchCapability) {
            return this._folderSource.getFolders([], FOLDER_FETCH_SIZE, 0).pipe(map(function (_a) {
                var count = _a.total, folders = _a.data;
                return ({ count: count, folders: folders, index: 0, ancestors: [] });
            }));
        }
        return this._folderSource.getFolderContext(this.selectedFolderId, FOLDER_FETCH_SIZE, INITIAL_SKIP_OFFSET);
    };
    FolderTreeStateService.prototype.ngOnDestroy = function () {
        this._destroyed$.next();
        this._destroyed$.complete();
    };
    FolderTreeStateService.prototype.toggleExpand = function (toggledFolder, toggledFolderIndex, ev) {
        var _this = this;
        if (ev) {
            ev.stopPropagation();
        }
        var toggledId = toggledFolder.id;
        this.loadingFolder$.next({ id: toggledId, value: true });
        if (this.isExpanded(toggledId)) {
            this.parentIdOfExpandedId.delete(toggledId);
        }
        else {
            this.parentIdOfExpandedId.set(toggledId, toggledFolder.parentId);
        }
        var currentFolder = this.containers.find(function (f) { return !!f.folderData && f.folderData.id === toggledId; }).folderData;
        this._announce(currentFolder);
        var shouldRemoveHighlight = this.highlighted$.value &&
            getParentIds(this.treeContainers, this.highlighted$.value.id).includes(toggledId);
        this._patch(Math.max(0, toggledFolderIndex - FOLDER_FETCH_SIZE / 2), FOLDER_FETCH_SIZE, function () { return true; }).pipe(finalize(function () { return _this.loadingFolder$.next({ value: false }); })).subscribe(function () {
            if (shouldRemoveHighlight) {
                _this.highlighted$.next(null);
            }
        });
    };
    FolderTreeStateService.prototype.rangeLoad = function (_a) {
        var _this = this;
        var start = _a.start, end = _a.end;
        if (!!this._searchValue$.value) {
            this._searchFolders(this.searchValue, FOLDER_FETCH_SIZE, start);
            return;
        }
        this.treeContainers
            .slice(start, end + 1)
            .forEach(function (f) { return f.loading = VirtualScrollItemStatus.pending; });
        this._treeContainers$.next(this.treeContainers);
        this._patch(start, end - start + 1, function (count) { return count !== _this.treeContainers.length; })
            .subscribe(function (wasInvalidated) {
            if (wasInvalidated) {
                var range = start > _this.activeIndex ?
                    { start: Math.max(0, start - FOLDER_FETCH_SIZE), end: start - 1 } :
                    { start: end + 1, end: Math.min(_this.treeContainers.length - 1, end + FOLDER_FETCH_SIZE) };
                _this.rangeLoad(range);
            }
        });
    };
    FolderTreeStateService.prototype.refresh = function () {
        var _this = this;
        if (this.initialLoading$.value) {
            return;
        }
        this._patch(Math.max(0, this.activeIndex - 5), FOLDER_FETCH_SIZE, function (count) { return count !== _this.treeContainers.length; }).pipe(takeUntil(this._patchStarted$)).subscribe();
    };
    FolderTreeStateService.prototype.patchFolder = function (changes) {
        var folder = this.folderById(changes.id);
        if (!folder) {
            return;
        }
        var folderData = folder.folderData;
        Object.assign(folderData, changes);
        this._treeContainers$.next(this.treeContainers);
        if (this.highlighted$.value &&
            this.highlighted$.value.id === changes.id) {
            this.highlighted$.next(cloneDeep(folderData));
        }
    };
    FolderTreeStateService.prototype.folderById = function (id) {
        var folderIndex = this.folderIndexById(id);
        if (folderIndex === -1) {
            return undefined;
        }
        return this.containers[folderIndex];
    };
    FolderTreeStateService.prototype.folderIndexById = function (id) {
        return this.containers.findIndex(function (c) { return c.folderData && c.folderData.id === id; });
    };
    FolderTreeStateService.prototype.navigate = function (direction, vs) {
        if (!this.containers.length) {
            return;
        }
        var currentHighlight = this.highlighted$.value;
        if (!currentHighlight) {
            var firstRenderedIndex = vs.getRenderedRange().start;
            var firstRendered = this.containers[firstRenderedIndex] && this.containers[firstRenderedIndex].folderData;
            if (!firstRendered) {
                return;
            }
            this.highlighted$.next(firstRendered);
            this._announce(firstRendered);
            vs.scrollToIndex(Math.max(firstRenderedIndex - 3, 0));
            return;
        }
        var currentIndex = Math.max(this.containers.findIndex(function (c) { return !!c.folderData && c.folderData.id === currentHighlight.id; }), 0);
        var nextIndex = currentIndex;
        var currentFolder = this.containers[currentIndex].folderData;
        if (direction === 'right') {
            if (currentFolder.hasChildren && !this.isExpanded(currentFolder.id)) {
                this.toggleExpand(currentFolder, currentIndex);
                return;
            }
            direction = 'down';
        }
        else if (direction === 'left') {
            if (this.isExpanded(currentFolder.id)) {
                this.toggleExpand(currentFolder, currentIndex);
                return;
            }
            var parentIndex = this.containers.findIndex(function (c) { return !!c.folderData && c.folderData.id === currentFolder.parentId; });
            if (parentIndex !== -1) {
                nextIndex = parentIndex;
            }
            else {
                direction = 'up';
            }
        }
        nextIndex +=
            direction === 'up'
                ? -1
                : direction === 'down'
                    ? 1
                    : 0;
        nextIndex = Math.min(Math.max(nextIndex, 0), this.containers.length - 1);
        var nextFolder = this.containers[nextIndex].folderData;
        if (!nextFolder) {
            return;
        }
        vs.scrollToIndex(Math.max(nextIndex - 3, 0));
        this.highlighted$.next(this.containers[nextIndex].folderData);
        this._announce(nextFolder);
    };
    FolderTreeStateService.prototype._checkHighlightValid = function () {
        if (!this.highlighted$.value) {
            return;
        }
        var highlightedFolder = this.folderById(this.highlighted$.value.id);
        if (!highlightedFolder) {
            this.highlighted$.next(null);
        }
    };
    FolderTreeStateService.prototype._announce = function (_a) {
        var displayName = _a.displayName, level = _a.level, fullyQualifiedName = _a.fullyQualifiedName, hasChildren = _a.hasChildren, id = _a.id;
        var params = {
            folder: this.isInSearchMode ? fullyQualifiedName : displayName,
            level: level,
        };
        var key = !!this.searchValue
            ? 'folder_tree_search_result'
            : !hasChildren
                ? 'folder_tree_folder_no_children'
                : this.isExpanded(id)
                    ? 'folder_tree_folder_expanded'
                    : 'folder_tree_folder_collapsed';
        this._liveAnnouncer.announce(this._translateService.instant(key, params));
    };
    return FolderTreeStateService;
}());
export { FolderTreeStateService };
